var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useState } from "react";
import { isUrlWhitelisted, PHONE_AUTH_STRATEGY } from "@jti/fe-common";
import { BrowserStorage, User } from "@jti/rest-api";
import { AlertBanner, Form, OtpComponent, toast, useConfig, useForm, useTranslations } from "@jti/ui";
import { useLogin } from "../../../hooks/useAuth";
import { useProfile } from "../../../hooks/useProfile";
import { getDevices } from "../LoginForm";
import styles from "./PhoneLogin.module.scss";
const CONSTANTS = {
    DEFAULT_ATTEMPTS: 3,
    RESEND_CODE_TIMEOUT: 180,
    FORM_DEFAULT_VALUES: {
        telephone: "",
        phone_validation_code: "",
    },
};
export const PhoneLogin = () => {
    const { __ } = useTranslations();
    const { REDIRECT_AFTER_LOGIN_URL, whitelistedDomains, PHONE_LOGIN_OTP_TIMEOUT, PHONE_LOGIN_OTP_NUMBER_OF_ATTEMPTS } = useConfig();
    const { sendOTC, checkPhone, getUserProfile } = useProfile();
    const { login } = useLogin();
    const otpTimeoutConfig = Number.parseInt(PHONE_LOGIN_OTP_TIMEOUT, 10);
    const otpTimeout = Number.isNaN(otpTimeoutConfig) ? CONSTANTS.RESEND_CODE_TIMEOUT : otpTimeoutConfig;
    const otpAttemptsConfig = Number.parseInt(PHONE_LOGIN_OTP_NUMBER_OF_ATTEMPTS, 10);
    const otpAttempts = Number.isNaN(otpAttemptsConfig) ? CONSTANTS.DEFAULT_ATTEMPTS : otpAttemptsConfig;
    const [verificationUuid, setVerificationUuid] = useState("");
    const [attempts, setAttempts] = useState(otpAttempts);
    const [isAttemptError, setIsAttemptError] = useState(false);
    const [isLoginButtonDisabled, setIsLoginButtonDisabled] = useState(false);
    const form = useForm({
        defaultValues: CONSTANTS.FORM_DEFAULT_VALUES,
    });
    const handleSendCode = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const phone = form.getValues("telephone");
            const { phone_verification_uuid } = yield sendOTC(phone);
            setVerificationUuid(phone_verification_uuid);
            toast.success(__("auth.login.phone.otp_code_sent"));
        }
        catch (error) {
            toast.error(__("auth.login.phone.otp_code_fail_to_send"));
        }
    }), [sendOTC, form, __]);
    const handleLoginSuccess = useCallback((returnUrl) => {
        if (returnUrl && isUrlWhitelisted(returnUrl, whitelistedDomains)) {
            window.location.href = returnUrl;
        }
        else {
            window.location.href = REDIRECT_AFTER_LOGIN_URL;
        }
    }, [whitelistedDomains, REDIRECT_AFTER_LOGIN_URL]);
    const handleSubmitOTP = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (attempts === 0)
            return;
        try {
            setIsAttemptError(false);
            setIsLoginButtonDisabled(true);
            const { telephone, phone_validation_code } = form.getValues();
            const response = yield login({
                strategy: PHONE_AUTH_STRATEGY,
                strategy_data: {
                    phone_number: telephone.startsWith("+") ? telephone : `+${telephone}`,
                    code: phone_validation_code,
                    action_verification_uuid: verificationUuid,
                },
            });
            const user = new User(new BrowserStorage());
            user.setUser(response);
            yield getUserProfile();
            yield getDevices(user);
            if (response.statusCode === 200 && response.uuid) {
                const searchParams = new URLSearchParams(window.location.search);
                const returnUrl = searchParams.get("returnUrl");
                handleLoginSuccess(returnUrl);
            }
        }
        catch (error) {
            setIsAttemptError(true);
            setAttempts((prev) => prev - 1);
        }
        finally {
            setIsLoginButtonDisabled(false);
        }
    }), [attempts, form, login, verificationUuid, getUserProfile, handleLoginSuccess]);
    const sendCodeBtnText = __("auth.login.phone.request_otc");
    return (_jsxs(Form, { className: styles.phoneLoginWrapper, form: form, onSubmit: handleSubmitOTP, dataTestId: "phoneLoginForm", children: [attempts === 0 && (_jsx(AlertBanner, { description: __("auth.login.sms_verify.too_many_attempts"), variant: "error", withIconClose: false })), _jsx(OtpComponent, { onSendCode: handleSendCode, onCheckPhone: checkPhone, isShowBorder: false, time: otpTimeout, isCounterEnabled: true, isOTPenabled: true, dataTestId: "phoneOtpInput", attemptsNum: attempts, isAttemptError: isAttemptError, onSubmitOTP: handleSubmitOTP, isPhoneValidatedViaSms: false, isInputNum: true, autocompleteType: "one-time-code", isSendCodeOnlyForExistingAccount: true, resendBtnNextLine: true, sendCodeBtnText: sendCodeBtnText, className: styles.otpWrapper, isShowLoginButton: true, isLoginButtonDisabled: isLoginButtonDisabled })] }));
};
